import React, { useEffect, useState, useRef, useMemo } from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { addYears, format } from "date-fns";
import Icon from "common/icon/icon.component";
import { smSize } from "common/icon/icon.constants";
import { isCreator } from "common/checkers/isCreator";
import DragAndDrop from "common/components/dragAndDrop";
import { useSnackbar } from "notistack";
import { Formik } from "formik";

import { MoreOptions, Loading, CheckBox, CheckType, Label, DatePiker } from "modules/primitives";
import { convertDateTimezone, shouldDownload } from "common/helper";
import { ACTIVITY_OPTIONS } from "../../../constants";
import styles from "./styles.module.scss";
import UploadedByInfo from "../../uploadedByInfo";
import { companyAgreementShouldOpen } from "modules/companies/company/company.action";
import AgreementTypes from "../../../popups/newItem/newAgreement/agreementTypes";
import { getCustomFieldAgreementType } from "modules/admin/adminCustomFieldsGroups/adminCustomField.action";
import useS3FileUpload from "../../../hooks/s3FileUplodar.hook";

const AgreementComponent = ({
  item,
  handleEditClick,
  blockFunctionality,
  handleDeleteClick,
}) => {
  const formRef = useRef(null);
  const agreementRef = useRef(null);
  const {
    authReducer: { session },
    companiesReducer: {
      companyReducer: {
        customFieldLoadingId,
        shouldOpenUnusedAgreement,
        selectedCompany,
        companyDetails,
      },
    },
    adminCustomFieldsReducer: {
      customFieldAgreementTypes,
    },
    councilReducer: { selectedCouncil },
    patientOrganizationsReducer: {
      patientOrganizationReducer: {
        selectedPatientOrganization,
        customFieldLoadingIdOrg,
      },
    },
  } = useSelector((state) => state);
  const dispatch = useDispatch();
  const [initModel, setInitModel] = useState({});
  const [hasAccessToEdit, setHasAccessToEdit] = useState(false);
  const [forbiddenFiles, setForbiddenFiles] = useState([]);
  const [docLoading, setDocLoading] = useState(false);
  const [showField, setShowField] = useState(false);
  const [checked, setChecked] = useState("");
  const [loadingId, setLoadingId] = useState("");
  const [fieldId, setFieldId] = useState("");
  const [uploadValues, setUploadValues] = useState([]);
  const { fileUpload, fileDelete } = useS3FileUpload();
  const { enqueueSnackbar } = useSnackbar();

  const onCheckBoxClick = (el) => {
    const agreementType = selectedCouncil.agreement_types?.filter((type) => type.name === el)[0];

    if (!agreementType) {
      return false;
    }

    dispatch(
      companyAgreementShouldOpen({
        type: agreementType.name,
        id: agreementType.id,
        mode: "edit",
        item,
      })
    );
  };

  useEffect(() => {
    dispatch(getCustomFieldAgreementType({ enqueueSnackbar }));
  }, []);

  useEffect(() => {
    if (item) {
      setChecked(item?.agreement_type_id || "");
      const newModel = {
        field_value: item.field_value,
        title: item.field_name,
        signed_at: item.signed_at || "",
        expiration_date: item.expiration_date || "",
        document_date: item.document_date || "",
        agreement_type_id: item.agreement_type_id,
        field_placeholder: item.field_placeholder || "",
      };

      setInitModel(newModel);
    }

    if (isCreator(item?.creator?.user_id)) {
      setHasAccessToEdit(true);
    }
  }, [item]);

  useEffect(() => {
    if (customFieldLoadingId || customFieldLoadingIdOrg) {
      if (selectedPatientOrganization?.id) {
        setLoadingId(customFieldLoadingIdOrg);
      } else {
        setLoadingId(customFieldLoadingId);
      }
    }
  }, [customFieldLoadingId, customFieldLoadingIdOrg, selectedPatientOrganization]);

  useEffect(() => {
    if (selectedPatientOrganization?.id) {
      setFieldId(item.patient_organization_custom_field_id);
    } else {
      setFieldId(item.company_custom_field);
    }
  }, [item, selectedPatientOrganization]);

  useEffect(() => {
    if (item.option_selected[0] && shouldOpenUnusedAgreement.type === item.option_selected[0]) {
      window.scrollTo({
        left: 0,
        top: agreementRef.current.offsetTop,
        behaviour: "smooth",
      });
    }
  }, [shouldOpenUnusedAgreement.type]);

  const getFilename = (url) => {
    if (url && typeof url === "string") {
      return url.split("/").pop();
    }
    return "";
  };

  const uploadHandler = async (e, action) => {
    if (forbiddenFiles.length) {
      setForbiddenFiles([]);
    }
    let files;

    if (action === "click") {
      if (!e || !e.target.files.length) {
        return;
      }

      files = Array.from(e.target.files);
    }

    if (action === "drop") {
      if (!e || !e.files.length) {
        return;
      }

      files = Array.from(e.files);
    }

    if (!files) {
      return;
    }
    setDocLoading(true);

    const fileList = [];
    const dismissedFiles = 0;

    setUploadValues((items) => {
      const currentFiles = [...items];
      const newFiles = files.map((file) => ({
        progress: 0,
        name: file.name,
      }));
      return [...currentFiles, ...newFiles];
    });
    files.forEach(async (file, index, array) => {
      const isLastIndex = Boolean(index === array.length - 1);
      if (file.size <= 20971520) {
        const pathname = `srm/${selectedCouncil.id}/${
          selectedPatientOrganization?.id ? "patient_organizations" : "companies"
        }/${selectedPatientOrganization?.id || selectedCompany.id}/`;
        await fileUpload(file, pathname, setUploadValues, file.name, 0).then((res) => {
          if (res && res.location) {
            isLastIndex && setDocLoading(false);
            fileList.push({
              added_by_id: session.id,
              name: getFilename(res.location),
              uri: res.location,
              council_id: selectedCouncil.id,
              document_resource_type: "CompanyCustomField",
              document_resource_id: item?.company_custom_field_id,
            });
            isLastIndex && setDocLoading(false);
            if (fileList.length === files.length - dismissedFiles) {
              // setAgreement([...agreement, ...fileList]);
            }
          }
        });
        setUploadValues([]);
      } else {
        setUploadValues((prevState) => {
          const currentProgress = [...prevState];
          const index = currentProgress.findIndex((item) => item.name === file.name);
          currentProgress[index] = {
            progress: "NOT_ALLOWED",
            name: file.name,
          };
          return currentProgress;
        });
        isLastIndex && setDocLoading(false);
        if (file.size > 10000000) {
          isLastIndex && setDocLoading(false);
          setForbiddenFiles(() => [
            {
              name: file.name,
              error: "Your file is too large. File size limit: 100MB",
            },
          ]);
          enqueueSnackbar(
            "We could not upload your document because your file size is too big. Please make sure the file is less than 20 MB",
            {
              variant: "error",
            }
          );
        }
      }
    });

    e.target.value = "";
  };

  const handleMoreOptionClick = (value) => {
    if (value === 0) {
      handleEditClick(item);
    }

    if (value === 1) {
      handleDeleteClick(item);
    }
  };

  const handleSelection = (val) => {
    if (checked === val.id) {
      setChecked("");
    } else {
      setChecked(val.id);
    }
  };

  const filterSelectedAgreementTypes = () => {
    const selectedAgreementTypesArray = [];
    companyDetails.forEach((companyDetail) => {
      if (companyDetail.field_type === "agreement") {
        customFieldAgreementTypes.forEach((CFAgreementType) => {
          if (CFAgreementType?.id === companyDetail.agreement_type_id) {
            selectedAgreementTypesArray.push(companyDetail.agreement_type_id);
          }
        });
      }
    });
    return selectedAgreementTypesArray;
  };

  const selectedCompanyAgreements = useMemo(
    () => filterSelectedAgreementTypes(),
    [customFieldAgreementTypes]
  );

  const onSubmit = (values) => {};

  const handleSetDate = (e, setFieldValue, type) => {
    setFieldValue(type, e);
  };

  const focusExpiryDate = (e, setFieldValue) => {
    if (formRef.current.values.document_date?.length) {
      handleSetDate(
        addYears(new Date(formRef.current.values.document_date), 1),
        setFieldValue,
        "expiration_date"
      );
    }
  };

  return (
    <div ref={agreementRef} className={styles.wrapper}>
      <div className={styles.titleRow}>
        <div className="d-flex flex-column">
          <h5 className={styles.title}>{item?.field_name || "Agreement"}</h5>
          {item?.field_placeholder?.length ? (
            <span className="text-muted small">{item.field_placeholder}</span>
          ) : null}
        </div>
        <div className={styles.rightWrp}>
          <div className={styles.colMore}>
            {!blockFunctionality && hasAccessToEdit ? (
              <MoreOptions
                options={ACTIVITY_OPTIONS}
                className={styles.moreOptWrp}
                onClick={handleMoreOptionClick}
                customSvgIcon
              />
            ) : null}
          </div>
        </div>
      </div>
      {item.from_claimed_profile && (
        <span className="d-block small font-weight-bold mb-3">Company Response</span>
      )}
      {fieldId === loadingId ? (
        <Loading />
      ) : showField ? null : (
        <div className={classNames("small", styles.description)}>
          <span className={styles.checkAgreementBox}>
            <CheckBox
              checkType={CheckType.BLUE}
              isChecked={item.agreement_type_selected}
              onClick={() => {
                onCheckBoxClick(item.agreement_type?.name, selectedCompany.agreement_tag);
              }}
            />
            <span>{item.agreement_type?.name}</span>
          </span>
          {/*<div className="cursor-pointer ml-2" onClick={() => setShowField(true)}>*/}
          {/*  <Icon*/}
          {/*    {...smSize}*/}
          {/*    icon="icn-edit"*/}
          {/*  />*/}
          {/*</div>*/}
        </div>
      )}

      {/*{*/}
      {/*  showField ? (*/}
      {/*    <Formik*/}
      {/*      innerRef={formRef}*/}
      {/*      enableReinitialize*/}
      {/*      initialValues={initModel}*/}
      {/*      onSubmit={(values, { setSubmitting, resetForm }) => {*/}
      {/*        setSubmitting(true);*/}
      {/*        onSubmit(values);*/}
      {/*        resetForm();*/}
      {/*      }}*/}
      {/*    >*/}
      {/*      {({ handleSubmit, setFieldValue, values, ...formikprops }) => (*/}
      {/*        <form onSubmit={handleSubmit}>*/}
      {/*          <div>*/}
      {/*            <div className="d-flex mb-4">*/}
      {/*              <div className="w-50">*/}
      {/*                {loadingAgreementType ? (*/}
      {/*                  <Loading />*/}
      {/*                ) : (*/}
      {/*                  <div className={styles.checkWrapper}>*/}
      {/*                    {customFieldAgreementTypes?.map((agreementTypeElement) => {*/}
      {/*                      return (*/}
      {/*                        <AgreementTypes*/}
      {/*                          initialAgreementType={agreementTypeElement}*/}
      {/*                          handleSelection={handleSelection}*/}
      {/*                          setFieldValue={setFieldValue}*/}
      {/*                          checked={item?.agreement_type_id}*/}
      {/*                          existingRecords={item?.existingRecords}*/}
      {/*                          optionsSelected={item?.option_selected}*/}
      {/*                          selectedCompanyAgreements={selectedCompanyAgreements}*/}
      {/*                          agreementTypeSelected={item?.agreement_type_selected}*/}
      {/*                          agreementIdFromOverview={item?.agreement_type_id}*/}
      {/*                        />*/}
      {/*                      );*/}
      {/*                    })}*/}
      {/*                  </div>*/}
      {/*                )}*/}
      {/*              </div>*/}
      {/*              <div className="w-50">*/}
      {/*                <Label>Signed On</Label>*/}
      {/*                <DatePiker*/}
      {/*                  name="document_date"*/}
      {/*                  value={values.document_date}*/}
      {/*                  formProps={{ ...formikprops, setFieldValue, values }}*/}
      {/*                  styleProp={styles.calendarStyle}*/}
      {/*                  className={styles.calendarBox}*/}
      {/*                  onChange={(e) => {*/}
      {/*                    setFieldValue("document_date", e);*/}
      {/*                  }}*/}
      {/*                  showTime*/}
      {/*                  withClear*/}
      {/*                />*/}
      {/*                <Label>Expires On</Label>*/}
      {/*                <DatePiker*/}
      {/*                  name="expiration_date"*/}
      {/*                  value={values.expiration_date}*/}
      {/*                  formProps={{ ...formikprops, setFieldValue, values }}*/}
      {/*                  styleProp={styles.calendarStyle}*/}
      {/*                  className={styles.calendarBox}*/}
      {/*                  onCalendarOpen={(e) => focusExpiryDate(e, setFieldValue)}*/}
      {/*                  onChange={(e) => {*/}
      {/*                    setFieldValue("expiration_date", e);*/}
      {/*                  }}*/}
      {/*                  showTime*/}
      {/*                  withClear*/}
      {/*                />*/}
      {/*              </div>*/}
      {/*            </div>*/}
      {/*            <DragAndDrop*/}
      {/*              uploadHandler={uploadHandler}*/}
      {/*              loading={docLoading}*/}
      {/*              customText="drag and drop documents here"*/}
      {/*              unicumKey={item?.custom_field_template_id}*/}
      {/*            />*/}

      {/*            {forbiddenFiles?.length ? (*/}
      {/*              <div className={styles.forbiddenfileContainer}>*/}
      {/*                Files that weren't allowed:{" "}*/}
      {/*                {forbiddenFiles.map((file) => (*/}
      {/*                  <span className={styles.forbiddenfileName}>*/}
      {/*                    {`${file.name}: `}*/}
      {/*                    <span className={styles.errorText}> {file.error}</span>*/}
      {/*                  </span>*/}
      {/*                ))}*/}
      {/*              </div>*/}
      {/*            ) : null}*/}
      {/*          </div>*/}
      {/*        </form>*/}
      {/*      )}*/}
      {/*    </Formik>*/}
      {/*  ) : null*/}
      {/*}*/}

      {item?.documents &&
        item?.documents.map((doc) => (
          <div
            key={doc.company_custom_field}
            className={classNames(styles.documnetContainer, doc.rfi ? styles.fromRfi : "")}
          >
            <Icon {...smSize} icon="icn-subnav-documents" className={styles.tileIcon} />
            <div className="d-flex align-items-center w-50">{doc.name}</div>
            <UploadedByInfo document={doc} />
            <div className={styles.rightWrp}>
              <a className={styles.docView} target="blank" href={doc.uri}>
                {shouldDownload(getFilename(doc.uri)) ? "download" : "view"}
              </a>
            </div>
          </div>
        ))}

      <div className={styles.dateWrp}>
        {item.document_date && (
          <div className={styles.dateField}>
            <span className={styles.label}>{`${"Signed on:"} `}</span>
            <span className={styles.marginL10px}> </span>
            <div className={styles.label}>
              {format(convertDateTimezone(item?.document_date), "EEEE, MMMM dd, yyyy")}
            </div>
          </div>
        )}
        {item.expiration_date && (
          <div className={styles.dateField}>
            <span className={styles.label}>Expires on: </span>
            <span className={styles.marginL10px}> </span>
            <div className={styles.label}>
              {format(convertDateTimezone(item.expiration_date), "EEEE, MMMM dd, yyyy ")}
            </div>
          </div>
        )}
      </div>

      {item && item.creator ? (
        <div className={styles.infoWrp}>
          <div className={styles.dataRow}>
            <div className={styles.label}>
              {item.from_submission
                ? "Submitted by"
                : item.updated_at !== item.created_at
                ? "Updated by"
                : "Created by"}
            </div>
            <div className={styles.value}>
              {item.from_submission ? (
                <span>{item?.creator?.full_name || "-"}</span>
              ) : (
                <Link to={`/people/internal/profile/${item.user_id}`}>
                  {item && item.creator && item.creator.full_name}
                  {item.from_claimed_profile && <span>, {selectedCompany?.name}</span>}
                </Link>
              )}
            </div>
          </div>
          {item.from_claimed_profile && (
            <div className="d-flex align-items-center mr-5">
              <div className={styles.companyAccess}>
                <span>&#x2713; Access</span>
              </div>
            </div>
          )}
          <div className={styles.date}>
            {item.updated_at !== item.created_at
              ? format(new Date(item.updated_at), "EEEE, MMMM dd, yyyy p")
              : format(new Date(item.created_at), "EEEE, MMMM dd, yyyy p")}
          </div>
          {item.from_submission ? (
            <div
              className={`d-flex flex-grow-1 align-items-center justify-content-center ${styles.companyResponse}`}
            >
              Company Response
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
export default React.memo(AgreementComponent);
